import React, { useMemo, useState } from 'react';
import { Check, Copy, BookOpen, Search } from 'lucide-react';
import ActionEditor from './ActionEditor';
import { STATE_WORDS, stateWord } from '../stateWords';
import { byGoalOrder } from '../goalOrder';
import ActionPage from './ActionPage';
import { fmtCivil } from '../dates';

// The Book (G) — the Goals surface after the redesign contest
// (docs/redesignContest.md, "The decision"). Two views in one component:
// the SHELF (quiet covers — name plus a one-line subtitle carrying the
// next open action and its world-owned date; the momentum dot at most;
// every cover at full presence — quiet is stated in prose on the spread,
// never by dimming the shelf) and the SPREAD (one goal open per
// viewport: the plan verso, the single next action and the projects
// recto; the road behind — the landing lines — one explicit page-turn
// deeper, never page one). Vocabulary per Nancy's amended law: plain
// labels everywhere a first-timer lands (plan / done / paused); fact
// lines carry only dates the world owns and absolute completed counts
// ("4 this week") — never denominators, targets, or streaks; the house
// voice survives only in full sentences.

const DAY = 24 * 60 * 60 * 1000;

// ── the shelf's own stylesheet ────────────────────────────────────────────
// Bound books of unequal size standing on a lit board, bottom-aligned on one
// baseline. Every ground, rule and paper here is mixed from the app's own
// tokens (--color-text / surface / background / primary / pho) with
// color-mix, so it survives all eleven themes, light and dark. Only the
// highlights and shadows are plain alpha white and black — a highlight is a
// highlight in every headspace, and a shadow is always the absence of light.
const SHELF_CSS = `
/* Grounds are mixed from tokens; the LIGHT is always alpha white and black on
   top of them, so the lit-above-left reading never inverts between a light
   theme and a dark one. */
.pk-shelf{
  text-align:left;
  --pk-tray-h:344px;
  --pk-wall:color-mix(in srgb, var(--color-text) 11%, var(--color-background));
  --pk-board:color-mix(in srgb, var(--color-primary) 24%, color-mix(in srgb, var(--color-text) 68%, var(--color-background)));
  --pk-band-bg:color-mix(in srgb, var(--color-text) 15%, var(--color-background));
  --pk-slip:color-mix(in srgb, var(--color-background) 80%, var(--color-surface));
  --pk-slip-rule:color-mix(in srgb, var(--color-text) 16%, transparent);
  --pk-paper:color-mix(in srgb, var(--color-background) 55%, var(--color-surface));
  --pk-paper-edge:color-mix(in srgb, var(--pk-paper) 86%, var(--color-text));
  --pk-soft:color-mix(in srgb, var(--color-text) 72%, transparent);
  --pk-faint:color-mix(in srgb, var(--color-text) 52%, transparent);
  --pk-rule:color-mix(in srgb, var(--color-text) 18%, transparent);
  --pk-contact:rgba(0,0,0,.34);
  --pk-cast:rgba(0,0,0,.26);
}
.pk-case{border-radius:10px 10px 12px 12px;overflow:hidden;box-shadow:0 26px 50px -28px var(--pk-cast)}
.pk-row{position:relative}
.pk-wall{
  padding:30px 22px 0;
  background:
    radial-gradient(900px 300px at 22% -40px, rgba(255,255,255,.16), rgba(0,0,0,0) 70%),
    linear-gradient(180deg, rgba(255,255,255,.06), rgba(0,0,0,.10)),
    var(--pk-wall);
  box-shadow:inset 0 -34px 44px -34px rgba(0,0,0,.30), inset 0 1px 0 rgba(255,255,255,.16);
}
.pk-books{display:flex;align-items:flex-end;gap:18px;flex-wrap:nowrap}

/* a book: width from what is in it, thickness from how many parts it has */
.pk-vol{
  position:relative;display:flex;flex:none;text-align:left;font:inherit;color:var(--color-text);
  background:none;border:0;padding:0;cursor:pointer;
  width:calc(var(--w) + var(--s));min-height:var(--h);
  border-radius:2px 5px 5px 2px;
  box-shadow:0 3px 4px -2px var(--pk-contact), 0 26px 32px -18px var(--pk-cast);
  transition:transform .22s cubic-bezier(.2,.7,.3,1), box-shadow .22s ease;
}
.pk-vol:hover{transform:translateY(-4px);box-shadow:0 4px 5px -2px var(--pk-contact),0 34px 40px -18px var(--pk-cast)}
.pk-vol:focus-visible{outline:2px solid var(--color-primary);outline-offset:5px}
.pk-vol[aria-expanded="true"]{transform:translateY(-6px)}
/* one light source, above-left: top wash, lit right shoulder, dark far edge */
.pk-vol::before{
  content:"";position:absolute;inset:0;border-radius:inherit;pointer-events:none;z-index:4;
  background:
    linear-gradient(180deg, rgba(255,255,255,.16), rgba(255,255,255,0) 20%),
    linear-gradient(0deg, rgba(0,0,0,.14), rgba(0,0,0,0) 9%),
    linear-gradient(90deg, rgba(255,255,255,0) 90%, rgba(255,255,255,.10) 97%, rgba(0,0,0,.22) 100%);
}
/* the page block on the outer side — it has pages, not just a face */
.pk-vol::after{
  content:"";position:absolute;top:3px;bottom:3px;right:-3px;width:3px;z-index:0;border-radius:0 2px 2px 0;
  background:repeating-linear-gradient(180deg, var(--color-surface) 0 2px, color-mix(in srgb, var(--color-surface) 74%, var(--color-text)) 2px 3px);
  box-shadow:1px 1px 2px var(--pk-contact);
}
/* layered material: two weaves over a three-stop ground */
.pk-mat{
  background:
    repeating-linear-gradient(0deg, rgba(255,255,255,.03) 0 1px, rgba(255,255,255,0) 1px 3px),
    repeating-linear-gradient(90deg, rgba(0,0,0,.045) 0 1px, rgba(0,0,0,0) 1px 3px),
    linear-gradient(160deg, var(--pk-a), var(--pk-b) 55%, var(--pk-c));
}
/* the turned-away face. It carries no type — titles read across the cover. */
.pk-spine{
  width:var(--s);flex:none;position:relative;overflow:hidden;border-radius:2px 0 0 2px;
  background:linear-gradient(90deg, rgba(0,0,0,.38) 0, rgba(0,0,0,.13) 42%, rgba(255,255,255,.11) 64%, rgba(0,0,0,.28) 100%);
}
.pk-spine::before,.pk-spine::after{
  content:"";position:absolute;left:0;right:0;height:4px;opacity:.5;
  background:repeating-linear-gradient(90deg, var(--pk-acc) 0 1px, rgba(0,0,0,.25) 1px 2px);
}
.pk-spine::before{top:0}
.pk-spine::after{bottom:0}
.pk-face{
  width:var(--w);flex:none;position:relative;overflow:hidden;border-radius:0 5px 5px 0;
  display:flex;flex-direction:column;gap:12px;padding:16px 15px 14px;
}
/* the counts and your last note always sit on the tail edge of the cover */
.pk-face > .pk-tail{margin-top:auto}
/* the hinge: a dark crease, a specular lift, then falloff */
.pk-face::before{
  content:"";position:absolute;inset:0;pointer-events:none;z-index:3;
  background:linear-gradient(90deg, rgba(0,0,0,.36) 0, rgba(0,0,0,.12) 5px, rgba(255,255,255,.16) 8px, rgba(255,255,255,0) 16px);
}
.pk-face > *{position:relative;z-index:2}

.pk-when{display:block;font-size:9px;letter-spacing:.2em;text-transform:uppercase;font-weight:600;color:var(--pk-acc)}
.pk-title{
  display:block;font-family:var(--font-display, Georgia, serif);font-weight:500;font-size:var(--ts);
  line-height:1.14;letter-spacing:.005em;margin-top:8px;overflow-wrap:normal;word-break:normal;hyphens:none;text-wrap:balance;color:var(--color-text);
}
.pk-head{display:block}

/* every starred action, on its own slip, under the project it lives in */
.pk-marks{display:flex;flex-direction:column;gap:7px}
.pk-mark{
  display:block;border-radius:1.5px;padding:7px 9px 8px;transform:rotate(-.5deg);
  background:linear-gradient(180deg, var(--pk-slip) 90%, color-mix(in srgb, var(--pk-slip) 88%, var(--color-text)));
  box-shadow:0 1px 2px rgba(0,0,0,.26), 0 8px 12px -8px rgba(0,0,0,.32);
}
.pk-mark:nth-child(even){transform:rotate(.45deg)}
.pk-mark-proj{
  display:block;font-size:8px;letter-spacing:.15em;text-transform:uppercase;
  color:color-mix(in srgb, var(--color-text) 58%, transparent);
  padding-bottom:4px;margin-bottom:5px;border-bottom:1px solid var(--pk-slip-rule);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
}
.pk-mark-act{display:block;font-size:11.5px;line-height:1.34;color:var(--color-text);padding-left:14px;text-indent:-14px}
.pk-mark-act + .pk-mark-act{margin-top:4px}
.pk-mark-act i{font-style:normal;color:var(--color-pho);font-size:10px;padding-right:5px}
.pk-nostar{display:block;font-size:11px;font-style:italic;color:var(--pk-faint)}

.pk-tail{display:block}
.pk-note{
  font-family:var(--font-display, Georgia, serif);font-style:italic;font-size:11.5px;line-height:1.4;
  color:var(--pk-soft);border-top:1px solid var(--pk-rule);padding-top:8px;
  display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;
}
.pk-count{
  display:block;font-size:9px;letter-spacing:.11em;text-transform:uppercase;color:var(--pk-faint);
  font-variant-numeric:tabular-nums;margin-top:6px;
}

/* how the title is put on — three bindings, stable per goal */
.pk-b-stamped .pk-title{color:var(--pk-acc);letter-spacing:.012em}
.pk-b-stamped .pk-face{padding-top:20px}
.pk-frame{position:absolute;inset:8px;border:1px solid var(--pk-rule);border-radius:2px;pointer-events:none;z-index:1}
.pk-frame::after{content:"";position:absolute;inset:3px;border:1px solid var(--pk-rule);opacity:.45;border-radius:1px}
.pk-b-label .pk-head{
  /* a paste-down label, not a sticky note: paper tinted by the book's own
     ground, a hairline edge, and the faint off-square of a hand-placed thing */
  background:linear-gradient(180deg, color-mix(in srgb, var(--pk-a, var(--pk-slip)) 16%, var(--pk-slip)) 92%, color-mix(in srgb, var(--pk-slip) 84%, var(--color-text)));
  border:1px solid color-mix(in srgb, var(--color-text) 13%, transparent);
  border-radius:1.5px;padding:10px 11px 12px;
  max-width:100%;box-sizing:border-box;
  transform:rotate(-.45deg);
  box-shadow:0 1px 2px rgba(0,0,0,.26), 0 7px 11px -8px rgba(0,0,0,.32);
}
/* thin books wear a smaller label — a plate must never overhang the fore-edge */
.pk-thin .pk-head{padding:7px 8px 8px;max-width:100%;box-sizing:border-box}
.pk-thin .pk-title{font-size:14px;line-height:1.25}

/* the bookend: closes the composition, marks where the next book goes */
.pk-bookend{flex:none;width:34px;height:112px;position:relative;align-self:flex-end;margin-left:4px}
.pk-bookend::before{
  content:"";position:absolute;left:0;bottom:0;width:9px;height:100%;border-radius:2px 2px 1px 1px;
  background:linear-gradient(180deg, color-mix(in srgb, var(--color-text) 58%, var(--color-surface)), color-mix(in srgb, var(--color-text) 76%, var(--color-background)));
  box-shadow:inset 0 1px 0 rgba(255,255,255,.22), 1px 2px 3px rgba(0,0,0,.28);
}
.pk-bookend::after{
  content:"";position:absolute;left:0;bottom:0;width:100%;height:7px;border-radius:1px;
  background:linear-gradient(90deg, color-mix(in srgb, var(--color-text) 68%, var(--color-background)), color-mix(in srgb, var(--color-text) 50%, var(--color-surface)));
  box-shadow:1px 1px 2px rgba(0,0,0,.22);
}
.pk-b-plain .pk-head{border-top:1px solid var(--pk-rule);padding-top:10px}

/* a finished goal lies flat */
.pk-flat{border-radius:3px;min-height:42px}
.pk-flat::after{
  top:auto;right:2px;left:2px;bottom:-3px;width:auto;height:3px;border-radius:0 0 2px 2px;
  background:repeating-linear-gradient(90deg, var(--color-surface) 0 2px, color-mix(in srgb, var(--color-surface) 74%, var(--color-text)) 2px 3px);
}
.pk-flat::before{background:linear-gradient(180deg, rgba(255,255,255,.20), rgba(255,255,255,0) 44%, rgba(0,0,0,.16) 100%)}
.pk-flat .pk-face{width:100%;flex-direction:row;align-items:center;gap:12px;padding:0 16px;border-radius:3px}
.pk-flat .pk-face::before{background:none}
.pk-flat .pk-title{margin-top:0;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex:1}
.pk-flat .pk-count{margin-top:0;white-space:nowrap}
.pk-tick{color:var(--pk-acc);font-size:11px;flex:none}

/* the board the books stand on, with the highlight along its front edge */
.pk-board{
  height:19px;position:relative;z-index:5;
  background:
    linear-gradient(180deg, rgba(255,255,255,.30) 0%, rgba(255,255,255,.08) 16%, rgba(0,0,0,.12) 62%, rgba(0,0,0,.34) 100%),
    var(--pk-board);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.5), inset 0 3px 5px -2px rgba(255,255,255,.2), 0 10px 14px -6px rgba(0,0,0,.30);
}
.pk-board::after{
  content:"";position:absolute;left:12px;right:12px;top:2px;height:1px;
  background:linear-gradient(90deg,transparent, rgba(255,255,255,.6) 14%, rgba(255,255,255,.6) 86%, transparent);
}

/* one click lays a book open beneath its own row; the rows below slide down
   as a block, always by the same distance, because this height is fixed */
.pk-band{
  height:0;overflow:hidden;transition:height .34s cubic-bezier(.4,0,.2,1);
  background:linear-gradient(180deg, rgba(0,0,0,.12), rgba(0,0,0,0) 45%), var(--pk-band-bg);
}
.pk-band.pk-open{height:calc(var(--pk-tray-h) + 48px)}
.pk-band-in{padding:24px 22px}
.pk-tray{
  position:relative;height:var(--pk-tray-h);border-radius:4px 4px 6px 6px;
  background:linear-gradient(180deg, var(--pk-paper) 94%, var(--pk-paper-edge));
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.28),
    0 2px 0 -1px color-mix(in srgb, var(--pk-paper-edge) 88%, var(--color-text)),
    0 4px 0 -2px color-mix(in srgb, var(--pk-paper-edge) 78%, var(--color-text)),
    0 6px 0 -3px color-mix(in srgb, var(--pk-paper-edge) 68%, var(--color-text)),
    0 3px 4px -2px var(--pk-contact),
    0 26px 40px -22px var(--pk-cast);
}
.pk-tray::before{
  content:"";position:absolute;top:0;bottom:0;left:34%;width:78px;transform:translateX(-50%);pointer-events:none;
  background:linear-gradient(90deg,transparent, rgba(0,0,0,.04) 40%, rgba(0,0,0,.09) 50%, rgba(0,0,0,.04) 60%, transparent);
}
.pk-tray::after{
  content:"";position:absolute;top:-9px;left:var(--pk-caret,80px);margin-left:-9px;
  border-left:9px solid transparent;border-right:9px solid transparent;border-bottom:9px solid var(--pk-paper);
  transition:left .26s cubic-bezier(.4,0,.2,1);
}
.pk-tray-body{display:grid;grid-template-columns:minmax(240px,34%) 1fr;height:100%;animation:pk-fade .18s ease}
@keyframes pk-fade{from{opacity:0}to{opacity:1}}
/* The boot settle (Nova, panel-approved; Paul: "I like a little magic and
   motion"). Once per session, on arrival: the books rise ~6px into place,
   staggered, 300ms, wordless — the app placing real objects, not a
   celebration. The lighting stays put (that half was rejected 4-0). */
@keyframes pk-settle{from{opacity:.35;transform:translateY(6px)}to{opacity:1;transform:none}}
.pk-settling .pk-vol{animation:pk-settle .3s ease-out backwards;animation-delay:calc(var(--settle-i,0)*45ms)}
.pk-pg{padding:24px 26px 22px;min-height:0;display:flex;flex-direction:column;overflow:auto}
.pk-pg-r{padding-left:34px}
.pk-pg h3:focus{outline:none}
.pk-pg h3:focus-visible{outline:2px solid var(--color-primary);outline-offset:4px;border-radius:3px}

.pk-contents{display:grid;align-content:start;gap:2px 30px;grid-template-columns:repeat(auto-fill,minmax(212px,1fr))}
.pk-proj{margin-bottom:15px}
.pk-proj h4{
  display:flex;align-items:baseline;gap:8px;font-weight:400;font-size:9px;letter-spacing:.17em;
  text-transform:uppercase;color:var(--color-textMuted);margin-bottom:6px;
}
.pk-proj h4 > span:first-child{flex:0 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.pk-proj h4 .pk-lead{flex:1 1 12px;min-width:12px;border-bottom:1px dotted color-mix(in srgb, var(--color-text) 22%, transparent);transform:translateY(-3px)}
.pk-proj h4 .pk-n{flex:none;white-space:nowrap;font-variant-numeric:tabular-nums;letter-spacing:.06em}
.pk-proj ul{list-style:none;margin:0;padding:0}
.pk-proj li{display:flex;gap:8px;align-items:baseline;font-size:12.5px;line-height:1.5;padding:1px 0}
.pk-li-t{flex:1;min-width:0}
.pk-state{flex:none;font-size:9px;letter-spacing:.09em;text-transform:uppercase;color:var(--color-textMuted)}
.pk-proj li.pk-done .pk-li-t{opacity:.6}

@media (max-width:900px){
  .pk-shelf{--pk-tray-h:560px}
  .pk-tray-body{grid-template-columns:1fr;grid-template-rows:auto 1fr}
  .pk-tray::before{display:none}
  .pk-pg-r{padding-left:26px;border-top:1px solid var(--pk-rule)}
  .pk-contents{grid-template-columns:1fr}
}
@media (prefers-reduced-motion: reduce){
  .pk-band,.pk-vol,.pk-tray::after,.pk-tray-body{transition:none;animation:none}
}
`;

// The settle plays once per app session — a module flag, not state, so
// switching views and coming back does not replay the performance.
let SETTLE_PLAYED = false;

const lastTouch = (g) => new Date(g.lastTouchedAt || g.completedDate || g.createdDate || 0).getTime();

function momentumColor(ms) {
  if (!ms) return 'var(--color-border)';
  const age = Date.now() - ms;
  if (age < 7 * DAY) return 'var(--color-success)';
  if (age < 30 * DAY) return 'var(--color-warning)';
  return 'var(--color-border)';
}

export default function Ledger({
  jump, posRef, holdSettle,
  goals, projects, actions, completedActions, trash,
  updateGoal, updateProject,
  deleteGoal, deleteProject,
  restoreFromTrash, deleteForever,
  onOpenAction, onExit, onHomeChange, onLogout, themeControl, openProjectSolo, openPlanting, openLibrary, onWriteFullscreen,
  updateAction, deleteAction, addActionToProject, addProjectInline,
  setActionStatus, toggleActionPho, toggleActionComplete, addChecklistItem,
  getProjectColorById, timeAgo,
}) {
  const activeGoals = goals.filter(i => i.active);
  const activeProjects = projects.filter(p => p.active);
  // Where you were last time. The component unmounts when you switch views,
  // so the parent holds this ref — returning to Goals resumes the same page
  // instead of dumping you back on the shelf.
  const freshJump = (jump && jump.type && (!posRef || posRef.current.lastJumpN !== jump.n)) ? jump : null;
  const remembered = (() => {
    if (freshJump) return null;
    const r = posRef && posRef.current;
    if (!r || !r.sel) return null;
    // don't resume a goal or project that has since been deleted
    const alive = r.sel.type === 'goal'
      ? goals.some(g => g.id === r.sel.id)
      : projects.some(p => p.id === r.sel.id);
    return alive ? r : null;
  })();
  // sel === null → the shelf; otherwise an open book {type, id}.
  // A 'home' jump is the whole shelf — G pressed while already in Goals.
  const [sel, setSel] = useState(() =>
    freshJump ? (freshJump.type === 'home' ? null : { type: freshJump.type, id: freshJump.id })
              : (remembered ? remembered.sel : null));
  // Deep links from the sidebar land on the right book even while mounted
  React.useEffect(() => {
    if (!jump || !jump.type) return;
    if (posRef && posRef.current.lastJumpN === jump.n) return; // already honoured
    if (posRef) posRef.current.lastJumpN = jump.n;
    setSel(jump.type === 'home' ? null : { type: jump.type, id: jump.id });
    setOpenActionId(null); setPageTwo(false);
    if (jump.type === 'home') setPeekId(null);
  }, [jump, posRef]);
  // The parent's unified header stands down while the home page shows —
  // the home draws its own top line, and a book brings the header back.
  React.useEffect(() => { if (onHomeChange) onHomeChange(!sel); }, [sel, onHomeChange]);
  // The question-home grid retired (the cure, Nov 4 2026): leaving a page
  // leaves the Ledger itself — back to the wall, never to the old grid.
  const goHome = () => { if (onExit) onExit(); else setSel(null); };
  const [statusCopied, setStatusCopied] = useState(false);
  const [closing, setClosing] = useState(false);
  const [landingText, setLandingText] = useState('');
  const [query, setQuery] = useState('');
  // The home page asks one question: what project do you want to work on?
  // The lens answers "all of them, or one goal's" — one token, a transient
  // picker, nothing standing (T5, carried over from the parked design round).
  const [lens, setLens] = useState(null);          // null = All, else a goal id
  const [lensOpen, setLensOpen] = useState(false);
  const lensRef = React.useRef(null);
  React.useEffect(() => {
    if (!lensOpen) return;
    const onDown = (e) => { if (lensRef.current && !lensRef.current.contains(e.target)) setLensOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setLensOpen(false); } };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey, true); };
  }, [lensOpen]);
  // The spread always opens on page one; the road behind is a deliberate
  // page-turn away — never the first thing a returning reader faces.
  const [pageTwo, setPageTwo] = useState(() => freshJump ? false : !!(remembered && remembered.pageTwo));
  // The recto travels; the verso stays. An action opens inside the book,
  // never by ejecting you out of it.
  const [openActionId, setOpenActionId] = useState(() =>
    freshJump ? null : ((remembered && remembered.openActionId) ?? null));
  // Keep the parent's memory current, so leaving mid-drill returns you here
  React.useEffect(() => {
    if (posRef) posRef.current = { ...posRef.current, sel, openActionId, pageTwo };
  }, [sel, openActionId, pageTwo, posRef]);
  // N starts a new thing in the context you're standing in — the project's
  // action, or the goal's project. Never while you're typing something else.
  const newActionRef = React.useRef(null);
  const newProjectRef = React.useRef(null);
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key !== 'n' && e.key !== 'N') return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const field = newActionRef.current || newProjectRef.current;
      if (!field) return;
      e.preventDefault();
      e.stopPropagation(); // the list's own N must not fire underneath
      field.focus();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
  // Law 5: the project ··· opens on a click, not on a passing cursor, and shuts
  // on the next click outside it or Esc. Esc is captured and stopped here so it
  // closes the menu without also stepping the reader back out of the book.
  const [pMenuOpen, setPMenuOpen] = useState(false);
  // Done — released (the cure, Nov 4 2026): a project can finish well, the
  // same shape as a goal — one click, one typed landing line. Paul's own
  // words ("released to prod", "on stage") live in the line, not in new
  // states; the three-state law is untouched. Archive remains for the
  // abandoned; this is for the finished.
  const [doneAsk, setDoneAsk] = useState(false);
  const [doneLanding, setDoneLanding] = useState('');
  const pMenuRef = React.useRef(null);
  React.useEffect(() => {
    if (!pMenuOpen) return;
    const onDown = (e) => { if (pMenuRef.current && !pMenuRef.current.contains(e.target)) setPMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setPMenuOpen(false); } };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey, true); };
  }, [pMenuOpen]);

  // Navigate the book: a new destination closes whatever page was open —
  // including a book left lying open on the shelf, and the ··· menu.
  const go = (next) => { setSel(next); setOpenActionId(null); setPageTwo(false); setPeekId(null); setPMenuOpen(false); };

  // ── the shelf's own state ──────────────────────────────────────────────
  // Which book is lying open (`peekId`), what is still painted while the
  // board slides shut (`ghostId`), and how much board there is to pack books
  // onto (`shelfWidth`).
  const [peekId, setPeekId] = useState(null);
  const [ghostId, setGhostId] = useState(null);
  // Boot settle: waits until nothing covers the shelf (the morning Rounds
  // greet on top of it), then plays once and never again this session.
  // Two effects on purpose: the starter waits a beat so a same-boot overlay
  // can claim the screen first (the Rounds auto-open a tick after mount —
  // starting instantly meant playing underneath them, and the dep-change
  // cleanup then cancelled the end-timer, stranding the class forever).
  // The finisher owns its own timer, keyed only on settling.
  const [settling, setSettling] = useState(false);
  React.useEffect(() => {
    if (SETTLE_PLAYED || holdSettle || sel) return;
    const start = setTimeout(() => { SETTLE_PLAYED = true; setSettling(true); }, 350);
    return () => clearTimeout(start);
  }, [holdSettle, sel]);
  React.useEffect(() => {
    if (!settling) return;
    const t = setTimeout(() => setSettling(false), 1300);
    return () => clearTimeout(t);
  }, [settling]);
  const shelfRef = React.useRef(null);
  const [shelfWidth, setShelfWidth] = useState(1100);
  React.useLayoutEffect(() => {
    const el = shelfRef.current;
    if (!el) return;
    const measure = () => setShelfWidth(Math.max(280, el.clientWidth - 44));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sel]);
  // let the closing spread finish its slide before it is thrown away
  React.useEffect(() => {
    if (peekId != null || ghostId == null) return;
    const t = setTimeout(() => setGhostId(null), 380);
    return () => clearTimeout(t);
  }, [peekId, ghostId]);
  // Esc shuts the open book — and must beat the app's own Esc, which would
  // otherwise leave Goals entirely.
  React.useEffect(() => {
    if (sel || peekId == null) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      const b = document.querySelector(`[data-pk-vol="${peekId}"]`);
      setPeekId(null);
      if (b) b.focus();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [sel, peekId]);

  // Esc inside the book steps BACK ONE PAGE — action → project → goal →
  // shelf — never out of Goals. One press used to eject to the List
  // (the app's global Esc stripped ledgerView): UX-4. Capture-phase and
  // stopped, so the global ladder never sees it. Not while writing, and
  // not while a ··· menu is open — the menu's own Esc owns that.
  React.useEffect(() => {
    if (!sel) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (document.querySelector('[role="menu"]')) return;
      e.preventDefault();
      e.stopPropagation();
      if (openActionId != null) { setOpenActionId(null); return; }
      if (pageTwo) { setPageTwo(false); return; }
      // The level above every book is the home grid now — Esc never detours
      // through a goal book you didn't come from (Paul, Oct 7 2026).
      goHome(); // the level above every page is the wall now
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [sel, openActionId, pageTwo, projects]);

  // Space is Quick Look: it takes you into a goal from the shelf, and all the
  // way back out from inside one. Never while writing — every editor, title,
  // and field on both surfaces must keep the key.
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== ' ' && e.code !== 'Space') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (sel) {
        // inside a goal → back to all of them
        e.preventDefault(); e.stopPropagation();
        go(null);
        return;
      }
      // on the shelf → open whichever book is peeked, else the focused one
      const focused = document.activeElement;
      const fromFocus = focused && focused.getAttribute && focused.getAttribute('data-pk-vol');
      const id = peekId != null ? peekId : (fromFocus != null ? parseInt(fromFocus, 10) : null);
      if (id == null || Number.isNaN(id)) return; // nothing chosen — don't guess
      e.preventDefault(); e.stopPropagation();
      go({ type: 'goal', id });
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, peekId]);

  // ── derived stats ──
  const statsFor = (actionFilter) => {
    const open = actions.filter(actionFilter);
    const done = completedActions.filter(actionFilter);
    const touch = Math.max(0, ...open.map(lastTouch), ...done.map(lastTouch));
    return { open: open.length, done: done.length, touch };
  };
  // Facts Nancy's law permits: the next date the world owns, and completed
  // counts stated absolutely. Absence is silence, never a zero.
  const factsFor = (actionFilter) => {
    const open = actions.filter(actionFilter);
    const done = completedActions.filter(actionFilter);
    const doneWeek = done.filter(g => {
      const t = new Date(g.completedDate || 0).getTime();
      return t > 0 && Date.now() - t < 7 * DAY;
    }).length;
    const dues = open.map(g => g.dueDate).filter(Boolean).sort();
    return { nextDue: dues[0] || null, doneWeek, doneTotal: done.length };
  };
  const projActions = (pid) => (g) => g.linkType === 'project' && g.linkId === pid;
  const goalActions = (oid) => {
    const pids = projects.filter(p => p.goalId === oid).map(p => p.id);
    return (g) => g.linkType === 'project' && pids.includes(g.linkId);
  };
  // Markdown down to the sentences a reader would actually say out loud
  const plainLines = (md) => (md || '')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/^[#>\s]*/gm, '')
    .replace(/[*_`]/g, '')
    .split('\n').map(l => l.trim()).filter(Boolean);

  // Everything finished here that carries a note, newest first
  const landingsFor = (gid) => completedActions.filter(goalActions(gid))
    .filter(g => g.resolutionComment)
    .sort((a, b) => new Date(b.completedDate || 0) - new Date(a.completedDate || 0));

  const gardening = useMemo(() => {
    const zombies = activeProjects.filter(p => {
      const s = statsFor(projActions(p.id));
      return s.open > 0 && s.touch > 0 && Date.now() - s.touch > 30 * DAY;
    });
    const emptyProjects = activeProjects.filter(p => statsFor(projActions(p.id)).open + statsFor(projActions(p.id)).done === 0);
    const emptyGoals = activeGoals.filter(o => !activeProjects.some(p => p.goalId === o.id));
    const orphans = actions.filter(g => g.linkType !== 'project' || !projects.some(p => p.id === g.linkId));
    return { zombies, emptyProjects, emptyGoals, orphans };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals, projects, actions, completedActions]);

  const writeClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); return true; } catch (e) { /* fall through */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed'; ta.style.top = '-1000px'; ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (e) { return false; }
  };

  // ── status markdown (US-3) — typeset against Nancy's amended law ──
  const copyStatus = async (obj) => {
    const objProjects = activeProjects.filter(p => p.goalId === obj.id);
    const f = factsFor(goalActions(obj.id));
    const head = [];
    if (f.nextDue) head.push(`due ${fmtCivil(f.nextDue)}`);
    if (f.doneWeek > 0) head.push(`${f.doneWeek} done this week`);
    else if (f.doneTotal > 0) head.push(`${f.doneTotal} done`);
    const lines = [`# ${obj.name} — status`, ''];
    if (head.length) lines.push(`**${head.join(' · ')}**`, '');
    for (const p of objProjects) {
      const pf = factsFor(projActions(p.id));
      // The status quotes the user's own stars, never a computed pick (law 4)
      const starred = actions.filter(projActions(p.id)).filter(a => a.pho && !a.completed);
      const bits = [];
      if (starred.length) bits.push(starred.map(a => `✦ ${a.text}`).join(' · '));
      if (pf.nextDue) bits.push(`due ${fmtCivil(pf.nextDue)}`);
      if (pf.doneWeek > 0) bits.push(`${pf.doneWeek} done this week`);
      else if (pf.doneTotal > 0) bits.push(`${pf.doneTotal} done`);
      lines.push(`- **${p.name}**${bits.length ? ` — ${bits.join(' · ')}` : ''}`);
    }
    const landings = landingsFor(obj.id).slice(0, 5);
    if (landings.length) {
      lines.push('', '## Recently finished', '');
      landings.forEach(g => lines.push(`- ~~${g.text}~~ — ${g.resolutionComment}`));
    }
    const ok = await writeClipboard(lines.join('\n') + '\n');
    setStatusCopied(ok ? 'ok' : 'fail');
    setTimeout(() => setStatusCopied(false), 2600);
  };

  // ── close ritual (US-4) ──
  const completeGoal = (obj) => {
    updateGoal(obj.id, { active: false, landing: landingText.trim() || null, closedDate: new Date().toISOString() });
    projects.filter(p => p.goalId === obj.id).forEach(p => updateProject(p.id, { active: false }));
    setClosing(false); setLandingText('');
    goHome(); // back to the wall, where it now rests among the done
  };

  // ── shared bits ──
  const InlineName = ({ value, onSave, className }) => (
    <h1
      className={`${className} focus:outline-none`}
      contentEditable suppressContentEditableWarning title="Click to rename"
      onBlur={(e) => { const t = e.target.innerText.trim(); if (t && t !== value) onSave(t); else e.target.innerText = value; }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); e.target.innerText = value; e.target.blur(); }
      }}
    >{value}</h1>
  );

  // One quiet line of fact under a title, governed by the signed law:
  // "Due Nov 17 · 4 this week" — dates the world owns, completed counts
  // stated absolutely. Never "N of M"; nothing rendered when nothing is.
  const FactLine = ({ filter }) => {
    const f = factsFor(filter);
    const parts = [];
    if (f.nextDue) parts.push(`Due ${fmtCivil(f.nextDue)}`);
    if (f.doneWeek > 0) parts.push(`${f.doneWeek} this week`);
    else if (f.doneTotal > 0) parts.push(`${f.doneTotal} done`);
    if (parts.length === 0) return null;
    return <p className="text-[11.5px] themed-text-muted tracking-wide py-2">{parts.join(' · ')}</p>;
  };

  // Hue per goal — same derivation as the sidebar circles (index × 47°)
  const hueOf = (gid) => {
    let idx = activeGoals.findIndex(g => g.id === gid);
    if (idx < 0) idx = Math.max(0, goals.findIndex(g => g.id === gid));
    return (idx * 47) % 360;
  };

  // Cover identity at scale — goals sharing an initial get two-letter
  // monograms ("NM", "ND") and spread hues (+40° per sibling) so no two
  // neighbouring covers read identical. Cheap; recomputed per render.
  const coverIdentity = (() => {
    const initialOf = (g) => ((g.name || '?').trim().charAt(0) || '?').toUpperCase();
    const byInitial = {};
    activeGoals.forEach(g => {
      const init = initialOf(g);
      (byInitial[init] = byInitial[init] || []).push(g.id);
    });
    const map = {};
    activeGoals.forEach(g => {
      const siblings = byInitial[initialOf(g)];
      const shared = siblings.length > 1;
      const words = (g.name || '?').trim().split(/\s+/).filter(Boolean);
      const mono = shared && words.length > 1
        ? (words[0].charAt(0) + words[1].charAt(0)).toUpperCase()
        : initialOf(g);
      map[g.id] = { mono, hueOffset: shared ? siblings.indexOf(g.id) * 40 : 0 };
    });
    return map;
  })();

  // Typographic book cover — Penguin-classics calm, no images.
  // Quiet by decree: the name, plus one subtitle line in the cover's own
  // type (the next open action and its world-owned date, when they
  // exist), and at most the momentum dot. Every cover at full presence —
  // quiet is stated in prose on the spread, never by dimming the shelf.
  const Cover = ({ goal, w = 136, h = 190, onClick }) => {
    const ident = coverIdentity[goal.id] || {};
    const hue = (hueOf(goal.id) + (ident.hueOffset || 0)) % 360;
    const s = statsFor(goalActions(goal.id));
    const mono = ident.mono || (goal.name || '?').charAt(0).toUpperCase();
    const longName = (goal.name || '').length > 24;
    // No computed pick in the tooltip either — the name is enough (law 4)
    return (
      <button onClick={onClick} title={goal.name}
        className="relative flex-shrink-0 text-left rounded-r-md transition-all hover:-translate-y-0.5 hover:shadow-md"
        style={{
          width: w, height: h,
          background: `color-mix(in srgb, hsl(${hue},45%,50%) 15%, var(--color-surface))`,
          borderLeft: `3px solid color-mix(in srgb, hsl(${hue},50%,35%) 70%, var(--color-surface))`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
        }}>
        <span className="absolute rounded-full" title="momentum"
          style={{ top: 6, right: 6, width: 6, height: 6, background: momentumColor(s.touch) }} />
        <span className="absolute flex items-center justify-center select-none"
          style={{
            top: 0, left: 0, right: 0, bottom: '33%',
            fontFamily: 'var(--font-display, Georgia, serif)',
            fontSize: Math.round(w * (mono.length > 1 ? 0.24 : 0.3)),
            letterSpacing: mono.length > 1 ? '0.02em' : undefined,
            color: `color-mix(in srgb, hsl(${hue},50%,45%) 55%, var(--color-text))`,
          }}>
          {mono}
        </span>
        {/* lower third: the name, then the subtitle in the cover's own type */}
        <span className="absolute left-0 right-0" style={{ bottom: 7, paddingLeft: 8, paddingRight: 8 }}>
          <span className="themed-text block leading-tight"
            style={{
              fontVariant: 'all-small-caps', letterSpacing: '0.06em',
              fontSize: w > 130 ? (longName ? 10.5 : 12) : (longName ? 9.5 : 10.5),
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', overflowWrap: 'normal', wordBreak: 'normal', hyphens: 'none',
            }}>
            {goal.name}
          </span>
          {/* the computed "Next: …" subtitle that sat here is gone — law 4 */}
        </span>
      </button>
    );
  };

  const selGoal = sel && sel.type === 'goal' ? goals.find(i => i.id === sel.id) : null;
  const selProject = sel && sel.type === 'project' ? projects.find(p => p.id === sel.id) : null;

  // ── Share this goal: what travels / what stays home ──────────────────────
  // Landing lines and stars never travel; statuses always reset to To Do.
  const [sharing, setSharing] = useState(false);
  const [shareCharter, setShareCharter] = useState(true);
  const [shareNotes, setShareNotes] = useState(false);
  const [shareDates, setShareDates] = useState(false);
  const [shareCompleted, setShareCompleted] = useState(false);
  const [shareDone, setShareDone] = useState(false);

  const buildShareBundles = (goal) => {
    const projs = projects.filter(p => p.goalId === goal.id && p.active !== false);
    return projs.map(p => ({
      goal: goal.name,
      ...(shareCharter && goal.notes ? { goalNotes: goal.notes } : {}),
      project: p.name,
      ...(shareCharter && p.notes ? { projectNotes: p.notes } : {}),
      actions: [
        ...actions.filter(a => a.linkType === 'project' && a.linkId === p.id),
        ...(shareCompleted ? completedActions.filter(a => a.linkType === 'project' && a.linkId === p.id) : []),
      ].map(a => ({
        title: a.text,
        ...(a.nextStep ? { nextStep: a.nextStep } : {}),
        ...(shareNotes && a.notes ? { notes: a.notes } : {}),
        status: 'todo',
        starred: false,
        ...(shareDates && a.doDate ? { doDate: a.doDate } : {}),
        ...(shareDates && a.dueDate ? { dueDate: a.dueDate } : {}),
        ...(a.checklist && a.checklist.length ? { checklist: a.checklist.map(c => ({ text: c.text, done: false })) } : {}),
      })),
    }));
  };

  const downloadShare = (goal) => {
    const bundles = buildShareBundles(goal);
    const fileSlug = goal.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'goal';
    const blob = new Blob([JSON.stringify(bundles, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${fileSlug}.phokii.json`; a.click();
    URL.revokeObjectURL(url);
    setShareDone(true); setTimeout(() => setShareDone(false), 4000);
  };

  // ── shelf partitions ──
  const q = query.trim().toLowerCase();
  const matches = (g) => !q || (g.name || '').toLowerCase().includes(q) || (g.notes || '').toLowerCase().includes(q);
  // Set-aside goals stand down by the USER'S hand — never by diagnosis
  // (extending the zombie rule to whole goals would be the app grading the
  // user). They live in the resting line with everything else quiet.
  const shelved = activeGoals.filter(g => !g.setAside).filter(matches);
  const setAsideGoals = activeGoals.filter(g => g.setAside).filter(matches);
  const finished = goals.filter(g => !g.active).filter(matches);

  // ═════════════════════════ THE SHELF ═════════════════════════
  // Marked Pages. Goals are bound books of unequal size standing on a lit
  // board, bottom-aligned on one baseline. Nothing on a cover is computed
  // and nothing is a score: the name (read across the cover, never turned on
  // its side), the date the world owns, plain counts, your own last finishing
  // note, and a slip for every action you starred — each slip headed by the
  // project the action lives in, so a star is never floating.
  //
  // Size is derived, never chosen:
  //   width     ← how many open actions are in it   (152 → 250px)
  //   thickness ← how many projects it has          (8 → 36px)
  //   height    ← the size it happens to be bound at: stable per goal, means
  //               nothing at all, and content can only push it taller.
  const volWidth = (nActions) => 152 + Math.min(nActions, 14) * 7;
  const volSpine = (nProjects) => 8 + Math.min(nProjects, 7) * 4;
  const volFormat = (id) => 248 + ((Math.abs(Number(id) || 0) * 3) % 5) * 21;
  const SHELF_GAP = 18;

  const describeGoal = (goal) => {
    // a finished goal keeps its projects even though they were closed with it
    const gProjects = projects.filter(p => p.goalId === goal.id && (goal.active ? p.active !== false : true));
    const openHere = actions.filter(goalActions(goal.id));
    const ident = coverIdentity[goal.id] || {};
    const marks = gProjects
      .map(p => ({ id: p.id, project: p.name, items: openHere.filter(a => a.linkId === p.id && a.pho) }))
      .filter(m => m.items.length > 0);
    return {
      id: goal.id, goal,
      hue: (hueOf(goal.id) + (ident.hueOffset || 0)) % 360,
      projects: gProjects,
      nOpen: openHere.length,
      nProjects: gProjects.length,
      facts: factsFor(goalActions(goal.id)),
      note: landingsFor(goal.id)[0] || null,
      marks,
    };
  };

  const standingVols = shelved.map(g => {
    const d = describeGoal(g);
    // A goal nothing has been written into yet is a slim blank notebook —
    // it has not earned a slab's width, and it never announces its own
    // emptiness in debug text (dignity pass, Sep 2026).
    const unwritten = d.nOpen === 0 && d.facts.doneTotal === 0;
    const w = unwritten ? 106 : volWidth(d.nOpen);
    return {
      ...d, unwritten, flat: false, w, s: volSpine(d.nProjects), h: volFormat(g.id),
      ts: w >= 220 ? 22 : w >= 196 ? 20 : w >= 176 ? 18.5 : 17,
      bind: ['stamped', 'label', 'plain'][Math.abs(Number(g.id) || 0) % 3],
    };
  });
  const lyingVols = finished.map(g => {
    const d = describeGoal(g);
    return {
      ...d, flat: true, s: 0, h: 42, ts: 14, bind: 'plain',
      w: Math.max(230, Math.min(440, 130 + (g.name || '').length * 8)),
    };
  });

  // Books fill a board, then start the next one — so it holds at five and at
  // twenty-five. Each book carries the x of its own centre so the open spread
  // can point back at it.
  const packRows = (list, room) => {
    const rows = []; let cur = []; let x = 0;
    list.forEach(v => {
      const vw = v.w + v.s;
      if (cur.length && x + SHELF_GAP + vw > room) { rows.push(cur); cur = []; x = 0; }
      if (cur.length) x += SHELF_GAP;
      cur.push({ ...v, cx: x + vw / 2 });
      x += vw;
    });
    if (cur.length) rows.push(cur);
    return rows;
  };
  const shelfRows = [...packRows(standingVols, shelfWidth), ...packRows(lyingVols, shelfWidth)];

  // The one piece of shelf state: which book is lying open. `shownId` lags
  // `peekId` by the length of the close animation so the spread doesn't blink
  // out before the board has finished sliding shut.
  const shownId = peekId != null ? peekId : ghostId;
  const openRow = peekId == null ? -1 : shelfRows.findIndex(r => r.some(v => v.id === peekId));
  const shownRow = shownId == null ? -1 : shelfRows.findIndex(r => r.some(v => v.id === shownId));
  const shownVol = shownRow < 0 ? null : shelfRows[shownRow].find(v => v.id === shownId);

  const openPeek = (id, fromKeyboard) => {
    setGhostId(id); setPeekId(id);
    if (fromKeyboard) {
      setTimeout(() => {
        const h = document.querySelector('[data-pk-head]');
        if (h) h.focus();
      }, 90);
    }
  };
  const closePeek = (refocus) => {
    const id = peekId;
    setPeekId(null);
    if (refocus && id != null) {
      const b = document.querySelector(`[data-pk-vol="${id}"]`);
      if (b) b.focus();
    }
  };

  const plainCount = (v) => {
    if (v.nOpen === 0) return null; // an empty book says nothing, twice over
    const a = `${v.nOpen} ${v.nOpen === 1 ? 'action' : 'actions'}`;
    return v.nProjects > 1 && v.w >= 190
      ? `${a} in ${v.nProjects} projects`
      : a;
  };

  // A book. One click lays it open — never hover, never a double click, and
  // never navigation: the way in lives inside the spread.
  const Book = (v) => {
    const g = v.goal;
    const ground = `hsl(${v.hue} 42% 52%)`;
    const deep = `hsl(${v.hue} 55% 40%)`;
    const doneOn = g.closedDate ? new Date(g.closedDate) : null;
    const world = v.flat
      ? (doneOn && !isNaN(doneOn) ? `Finished ${doneOn.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : 'Finished')
      : (v.facts.nextDue ? `Due ${fmtCivil(v.facts.nextDue)}` : null);
    return (
      <button
        type="button"
        key={g.id}
        data-pk-vol={g.id}
        aria-expanded={peekId === g.id}
        title={`${g.name} — click to see what's inside`}
        onClick={(e) => (peekId === g.id ? closePeek(e.detail === 0) : openPeek(g.id, e.detail === 0))}
        className={`pk-vol pk-mat pk-b-${v.bind}${v.flat ? ' pk-flat' : ''}${v.unwritten ? ' pk-thin' : ''}`}
        style={{
          '--w': `${v.w}px`, '--s': `${v.s}px`, '--h': `${v.h}px`, '--ts': `${v.ts}px`,
          '--settle-i': v.settleIndex ?? 0,
          // How strongly the hue prints is a theme's choice: quiet stock by
          // default, saturated blocks in Poster.
          '--pk-a': `color-mix(in srgb, ${ground} var(--pk-mixA, 20%), var(--color-surface))`,
          '--pk-b': `color-mix(in srgb, ${ground} var(--pk-mixB, 28%), var(--color-surface))`,
          '--pk-c': `color-mix(in srgb, ${ground} var(--pk-mixC, 38%), var(--color-surface))`,
          '--pk-acc': `color-mix(in srgb, ${deep} var(--pk-mixAcc, 62%), var(--color-text))`,
        }}>

        {v.flat ? (
          <span className="pk-face">
            <span className="pk-tick" aria-hidden="true">✓</span>
            <span className="pk-title">{g.name}</span>
            <span className="pk-count">{world}</span>
          </span>
        ) : (<>
          <span className="pk-spine" aria-hidden="true" />
          <span className="pk-face">
            {v.bind === 'stamped' && <span className="pk-frame" aria-hidden="true" />}

            {/* the head: the date the world owns, then the name, set across */}
            <span className="pk-head">
              {world && <span className="pk-when">{world}</span>}
              <span className="pk-title">{g.name}</span>
            </span>

            {/* every starred action, on a slip under the project it lives in */}
            {v.marks.length > 0 ? (
              <span className="pk-marks">
                {v.marks.map(m => (
                  <span className="pk-mark" key={m.id}>
                    <span className="pk-mark-proj">{m.project}</span>
                    {m.items.map(a => (
                      <span className="pk-mark-act" key={a.id}>
                        <i aria-hidden="true">✦</i>{a.text}
                      </span>
                    ))}
                  </span>
                ))}
              </span>
            ) : v.unwritten ? null : (
              <span className="pk-nostar">nothing starred yet</span>
            )}

            {/* the tail: your own last words, then plain counts */}
            <span className="pk-tail">
              {v.note && <span className="pk-note">“{v.note.resolutionComment}”</span>}
              <span className="pk-count">
                {[plainCount(v), v.facts.doneWeek > 0 ? `${v.facts.doneWeek} finished this week` : null]
                  .filter(Boolean).join(' · ')}
              </span>
            </span>
          </span>
        </>)}
      </button>
    );
  };

  // The spread: the book laid open on the board under its own row.
  const Spread = (v) => {
    const g = v.goal;
    const doneHere = completedActions.filter(goalActions(g.id));
    const trail = landingsFor(g.id).slice(0, 3);
    const caretX = Math.max(34, Math.min(shelfWidth - 34, v.cx));
    const planText = plainLines(g.notes).join(' ');
    const plan = planText ? planText.slice(0, 260) + (planText.length > 260 ? '…' : '') : '';
    return (
      <div className="pk-tray" role="region" aria-label={g.name} style={{ '--pk-caret': `${caretX}px` }}>
        <div className="pk-tray-body" key={g.id}>

          <div className="pk-pg">
            <p className="text-[9px] uppercase tracking-[0.2em] themed-text-muted">{g.active ? 'Goal' : 'Finished goal'}</p>
            <h3 data-pk-head tabIndex={-1} className="mt-2 text-[23px] leading-tight themed-text"
              style={{ fontFamily: 'var(--font-display, Georgia, serif)', fontWeight: 500 }}>{g.name}</h3>
            <div className="mt-3 grid gap-1 text-[12.5px] themed-text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {v.facts.nextDue && <div>Due {fmtCivil(v.facts.nextDue)}</div>}
              <div>{v.nOpen} {v.nOpen === 1 ? 'action' : 'actions'}{v.nProjects > 0 ? ` in ${v.nProjects} ${v.nProjects === 1 ? 'project' : 'projects'}` : ''}</div>
              {v.facts.doneWeek > 0 && <div>{v.facts.doneWeek} finished this week</div>}
            </div>
            {!g.active && g.landing && (
              <p className="mt-4 italic text-[13px] themed-text leading-relaxed"
                style={{ fontFamily: 'var(--font-display, Georgia, serif)' }}>“{g.landing}”</p>
            )}
            {plan && <p className="mt-4 text-[12.5px] themed-text-muted leading-relaxed">{plan}</p>}
            {trail.length > 0 && (
              <div className="mt-5">
                <p className="text-[8.5px] uppercase tracking-[0.18em] themed-text-muted">Recently finished</p>
                {trail.map(a => (
                  <div key={a.id} className="mt-2.5">
                    <p className="italic text-[13px] themed-text leading-snug"
                      style={{ fontFamily: 'var(--font-display, Georgia, serif)' }}>“{a.resolutionComment}”</p>
                    <p className="text-[11px] themed-text-muted mt-0.5">— you, {timeAgo(a.completedDate || a.lastTouchedAt)}</p>
                  </div>
                ))}
              </div>
            )}
            {/* the only way into the goal itself */}
            <div className="mt-auto pt-5 flex gap-2 flex-wrap">
              <button type="button" onClick={() => go({ type: 'goal', id: g.id })}
                className="px-4 py-2 text-[13px] font-semibold rounded-lg themed-button-primary">Open</button>
              <button type="button" onClick={() => closePeek(true)}
                className="px-3 py-2 text-[12.5px] rounded-lg border themed-border themed-text-muted hover:themed-text">Close</button>
            </div>
          </div>

          <div className="pk-pg pk-pg-r">
            <p className="text-[9px] uppercase tracking-[0.2em] themed-text-muted mb-3.5">Projects</p>
            {v.projects.length === 0 && <p className="text-[12.5px] themed-text-muted">No projects yet.</p>}
            <div className="pk-contents">
              {v.projects.map(p => {
                const openA = actions.filter(a => a.linkType === 'project' && a.linkId === p.id);
                const doneA = doneHere.filter(a => a.linkId === p.id);
                const n = openA.length + doneA.length;
                return (
                  <div className="pk-proj" key={p.id}>
                    <h4>
                      <span>{p.name}</span>
                      <span className="pk-lead" aria-hidden="true" />
                      <span className="pk-n">{n} {n === 1 ? 'action' : 'actions'}</span>
                    </h4>
                    <ul>
                      {openA.map(a => (
                        <li key={a.id}>
                          <span className="pk-li-t themed-text">
                            {a.pho && <span style={{ color: 'var(--color-pho)' }} aria-hidden="true">✦ </span>}{a.text}
                          </span>
                          <span className="pk-state">{stateWord(a)}</span>
                        </li>
                      ))}
                      {doneA.map(a => (
                        <li key={`d-${a.id}`} className="pk-done">
                          <span className="pk-li-t themed-text">{a.text}</span>
                          <span className="pk-state">done</span>
                        </li>
                      ))}
                      {n === 0 && <li><span className="pk-li-t themed-text-muted">No actions here yet.</span></li>}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    );
  };

  // The home grid: every active project as one identical card, alphabetical,
  // filtered by the lens and the search box. Size never encodes data.
  const gridCards = activeProjects
    .filter(pr => lens == null || pr.goalId === lens)
    .filter(pr => !q || pr.name.toLowerCase().includes(q))
    .sort((a, b) => a.name.localeCompare(b.name));
  const lensGoal = lens == null ? null : activeGoals.find(g => g.id === lens);
  const openCount = (pid) => actions.filter(a => a.linkType === 'project' && a.linkId === pid && !a.completed).length;
  const doingCount = (pid) => actions.filter(a => a.linkType === 'project' && a.linkId === pid && !a.completed && a.status === 'doing').length;

  // The home page, after the comp: one wordmark, one lens, one question,
  // one grid of identical cards — and nothing else asking for anything.
  // The paused strip, orphans, Recently deleted and the library link left
  // this page on Paul's word (Oct 7 2026); paused pick-up and the library
  // still need a home somewhere — noted in the build notes.
  if (!sel) return (
    <div className="pk-shelf flex-1 min-w-0 overflow-y-auto px-10 pt-5 pb-24 themed-background"
      style={{
        minHeight: '100vh',
        backgroundImage: 'linear-gradient(color-mix(in srgb, var(--color-text) 5%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in srgb, var(--color-text) 5%, transparent) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }}>
      <style>{SHELF_CSS}</style>

      {/* The comp's masthead: wordmark and lens on the left, the room's two
          controls on the right. No nav bar — the grid is the nav. */}
      <div className="flex items-center gap-3 mb-14">
        <span className="text-[15px] font-bold themed-text select-none"
          style={{ fontFamily: "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace", letterSpacing: '0.03em' }}>
          phokii3
        </span>
        <span className="flex-1" />
        {themeControl}
        {onLogout && (
          <button onClick={onLogout} className="text-xs themed-text-muted hover:themed-text">log out</button>
        )}
      </div>

      {/* The one question, center stage */}
      <div className="mx-auto mb-14 w-full" style={{ maxWidth: 760 }}>
        <div className="relative">
          <span className="absolute left-5 top-1/2 -translate-y-1/2 text-[15px] themed-text-muted pointer-events-none select-none">›</span>
          <input type="text" value={query} onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && gridCards.length > 0) openProjectSolo(gridCards[0].id); }}
            placeholder="What project do you want to work on?"
            className="w-full text-[15px] themed-text themed-surface border themed-border rounded-xl focus:outline-none"
            style={{ padding: '15px 64px 15px 42px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }} />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] themed-text-muted border themed-border rounded px-1.5 py-0.5 pointer-events-none select-none">⌘K</span>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-3">
        <p className="text-[10.5px] font-bold uppercase themed-text-muted select-none" style={{ letterSpacing: '0.18em' }}>Projects</p>
        {/* the lens: All, or one goal — a word above the grid it filters (moved
            here from the masthead on Paul's word, Oct 7 2026) */}
        <div ref={lensRef} className="relative flex-shrink-0">
          <button onClick={() => setLensOpen(o => !o)} aria-expanded={lensOpen} aria-haspopup="menu"
            className="px-3 py-1 text-[12px] font-medium rounded-full border themed-border themed-text themed-surface hover:themed-surface-hover"
            title="See all projects, or one goal's">
            {lensGoal ? lensGoal.name : 'All'} <span className="text-[9px] themed-text-muted">▾</span>
          </button>
          {lensOpen && (
            <div role="menu" className="absolute left-0 top-8 themed-background border themed-border rounded-lg shadow-lg py-1 z-30 min-w-[200px]">
              <button onClick={() => { setLens(null); setLensOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-[12.5px] themed-text hover:themed-surface-hover">All</button>
              {[...activeGoals].sort(byGoalOrder).map(g => (
                <button key={g.id} onClick={() => { setLens(g.id); setLensOpen(false); }}
                  className="w-full text-left px-3 py-1.5 text-[12.5px] themed-text hover:themed-surface-hover truncate">{g.name}</button>
              ))}
            </div>
          )}
        </div>
        {openPlanting && (
          <button onClick={openPlanting} className="ml-auto text-[11px] themed-text-muted hover:themed-text" title="New goal (W)">+ new</button>
        )}
      </div>

      {/* Uniform cards, alphabetical — size never encodes data.
          Tasks live one click away, never here. */}
      {gridCards.length > 0 && (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {gridCards.map(pr => {
            const o = openCount(pr.id); const d = doingCount(pr.id);
            return (
              <button key={pr.id} onClick={() => openProjectSolo(pr.id)}
                className="text-left rounded-lg border themed-border themed-surface hover:themed-surface-hover transition-colors flex flex-col justify-between"
                style={{ height: 110, padding: '14px 16px', borderLeftWidth: 3, borderLeftColor: pr.color || 'var(--color-border)', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <span className="text-[14px] font-semibold themed-text leading-snug" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{pr.name}</span>
                <span className="text-[11.5px] themed-text-muted" style={{ fontFamily: "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace" }}>
                  {o > 0
                    ? <><span className="font-semibold themed-text">{o}</span> {STATE_WORDS.todo}{d > 0 && <> · <span className="font-semibold themed-text">{d}</span> {STATE_WORDS.doing}</>}</>
                    : 'nothing to do'}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {gridCards.length === 0 && q && (
        <p className="text-[13px] themed-text-muted py-10">Nothing matches “{query.trim()}”{lensGoal ? ` in ${lensGoal.name}` : ''}.</p>
      )}
      {gridCards.length === 0 && !q && (
        <div className="flex flex-col items-center justify-center text-center py-24">
          <p className="text-sm themed-text-muted mb-4">
            {lensGoal ? `${lensGoal.name} has no projects yet.` : 'No projects yet.'}
          </p>
          {openPlanting && (
            <button onClick={openPlanting}
              className="px-4 py-2 text-[13px] font-semibold rounded-lg themed-button-primary"
              title="New goal (W)">Start something</button>
          )}
        </div>
      )}
    </div>
  );

  // ═════════════════════════ THE SPREAD ═════════════════════════
  // One object per viewport. Page one: the plan (verso) · the single
  // next action and the projects (recto). Page two — one explicit
  // page-turn deeper — the road behind: the landing lines, in your own
  // words, "— you, last Thursday."
  // Inside a book the goal is the anchor: the verso never leaves while the
  // recto travels goal → project → action. (Founder's grounding critique.)
  const bookGoal = selGoal || (selProject ? goals.find(i => i.id === selProject.goalId) : null);
  const openAction = openActionId != null
    ? [...actions, ...completedActions].find(g => g.id === openActionId) || null
    : null;

  return (
    <div className="flex-1 min-w-0 overflow-y-auto px-10 pt-8 pb-24" style={{ minHeight: 'calc(100vh - 56px)', maxWidth: '1560px' }}>

      {bookGoal && (() => {
        const selGoalOrBook = bookGoal;
        const s = statsFor(goalActions(selGoalOrBook.id));
        const objProjects = activeProjects.filter(p => p.goalId === selGoalOrBook.id);
        // The user's stars, in their own order — never a computed pick (law 4)
        const starredHere = actions.filter(goalActions(selGoalOrBook.id))
          .filter(a => a.pho && !a.completed)
          .sort((a, b) => ((a.order ?? 0) - (b.order ?? 0)) || (a.id - b.id));
        const trail = landingsFor(selGoalOrBook.id);
        const planExcerpt = plainLines(selGoalOrBook.notes).join(' ').slice(0, 190);
        const goHome = () => go({ type: 'goal', id: selGoalOrBook.id });
        return (
        <div className="flex gap-8 items-start">

          {/* ── verso: the spine. Stays put while the recto travels. ── */}
          <div className="flex-shrink-0" style={{ width: '256px', position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
            <button onClick={goHome} className="text-[11px] themed-text-muted hover:themed-text mb-3">‹ Back</button>
            <div className="flex items-start gap-4">
              <Cover goal={selGoalOrBook} w={72} h={100} onClick={goHome} />
              <div className="flex-1 min-w-0">
                <InlineName value={selGoalOrBook.name} onSave={(t) => updateGoal(selGoalOrBook.id, { name: t })}
                  className="text-[17px] font-bold themed-text leading-snug" />
                <FactLine filter={goalActions(selGoalOrBook.id)} />
              </div>
            </div>
            {!selGoalOrBook.active && selGoalOrBook.landing && (
              <p className="italic text-[12px] themed-text-muted leading-relaxed mt-2">“{selGoalOrBook.landing}”</p>
            )}
            <button onClick={goHome} className="block text-left mt-4 w-full group/plan" title="Open the goal's plan to read or edit it">
              <span className="text-[10px] uppercase tracking-widest themed-text-muted font-semibold flex items-center gap-1.5">
                Plan
                <span className="opacity-0 group-hover/plan:opacity-100 transition-opacity normal-case tracking-normal font-normal themed-primary">edit</span>
              </span>
              {planExcerpt ? (
                <span className="block mt-1 text-[12px] themed-text-muted leading-relaxed group-hover/plan:themed-text transition-colors">
                  {planExcerpt}{planExcerpt.length >= 190 ? '…' : ''}
                </span>
              ) : (
                <span className="block mt-1 text-[12px] themed-text-muted italic leading-relaxed">
                  Nothing written yet — why does this goal exist?
                </span>
              )}
            </button>
            <div className="flex items-center gap-1.5 flex-wrap mt-4">
              <button onClick={() => copyStatus(selGoalOrBook)}
                className="flex items-center gap-1.5 px-2 py-1 rounded-full text-[10.5px] font-medium border themed-border themed-text-muted hover:themed-text transition-colors"
                title="Copy a status update as Markdown — your starred actions per project, dates, and what you finished recently. Paste it into a standup or an update.">
                {statusCopied === 'ok' ? <><Check size={10} style={{ color: 'var(--color-success)' }} /> copied</>
                  : statusCopied === 'fail' ? <><Copy size={10} /> try again</>
                  : <><Copy size={10} /> status</>}
              </button>
              <button onClick={() => setSharing(v => !v)}
                className="px-2 py-1 rounded-full text-[10.5px] font-medium border themed-border themed-text-muted hover:themed-text transition-colors"
                title="Export this goal as a plain file anyone can plant — or hand to any LLM">share…</button>
              <button onClick={() => setClosing(c => !c)}
                className="px-2 py-1 rounded-full text-[10.5px] font-medium border themed-border themed-text-muted hover:themed-text transition-colors"
                title="Complete this goal — it ends, honestly, with a landing line">complete…</button>
              <button
                onClick={() => {
                  const was = !!selGoalOrBook.setAside;
                  updateGoal(selGoalOrBook.id, { setAside: !was });
                  if (!was) goHome(); // it leaves the standing shelf; follow it out
                }}
                className="px-2 py-1 rounded-full text-[10.5px] font-medium border themed-border themed-text-muted hover:themed-text transition-colors"
                title={selGoalOrBook.setAside
                  ? 'Put this book back on the standing shelf'
                  : 'Set this book aside — it rests at the foot of the shelf, off the standing row, until you pick it back up'}>
                {selGoalOrBook.setAside ? 'put it back' : 'set aside'}</button>
            </div>
            {statusCopied && (
              <p className="text-[10px] themed-text-muted mt-1.5 leading-snug">
                {statusCopied === 'ok'
                  ? 'Status copied to your clipboard — paste it anywhere.'
                  : 'Couldn’t reach the clipboard — click the page, then try again.'}
              </p>
            )}

            {/* the other chapters, always in view */}
            <p className="text-[10px] uppercase tracking-widest themed-text-muted font-semibold mt-6 mb-1">Projects</p>
            {objProjects.length === 0 && <p className="text-[12px] themed-text-muted">No projects yet.</p>}
            {objProjects.map(p => {
              const here = selProject && selProject.id === p.id;
              const ps = statsFor(projActions(p.id));
              return (
                <button key={p.id}
                  onClick={() => openProjectSolo(p.id)}
                  className={`w-full text-left flex items-center gap-2 py-1.5 pl-2 pr-1 rounded transition-colors ${here ? 'themed-surface' : 'hover:themed-surface-hover'}`}
                  style={here ? { boxShadow: 'inset 2px 0 0 var(--color-primary)' } : {}}>
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: getProjectColorById(p.id) }} />
                  <span className={`text-[12.5px] flex-1 min-w-0 truncate ${here ? 'themed-text font-medium' : 'themed-text-secondary'}`}>{p.name}</span>
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" title="momentum" style={{ background: momentumColor(ps.touch) }} />
                </button>
              );
            })}
            {addProjectInline && (
              <input
                ref={newProjectRef}
                key={`np-${selGoalOrBook.id}-${objProjects.length}`}
                placeholder="+ New project…"
                title={`New project in ${selGoalOrBook.name}`}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { e.target.value = ''; e.target.blur(); return; }
                  if (e.key !== 'Enter') return;
                  const v = e.target.value.trim();
                  if (!v) return;
                  addProjectInline(v, selGoalOrBook.id);
                  e.target.value = '';
                }}
                className="w-full text-[12px] themed-text bg-transparent border-0 focus:outline-none py-1.5 pl-2 mt-1" />
            )}
            <button onClick={() => { go({ type: 'goal', id: selGoalOrBook.id }); setPageTwo(true); }}
              className="text-[11.5px] themed-text-muted hover:themed-text mt-5"
              title="Everything you finished here, in your own words">
              Finished{trail.length > 0 ? ` — ${trail.length}` : ''} ⟶
            </button>
          </div>

          {/* ── recto: the traveling page ── */}
          <div className="flex-1 min-w-0">
          {sharing && (() => {
            const b = buildShareBundles(selGoalOrBook);
            const nActions = b.reduce((n, x) => n + x.actions.length, 0);
            return (
              <div className="my-3 rounded-lg border themed-border p-4" style={{ borderLeft: '3px solid var(--color-primary)' }}>
                <p className="text-[12px] themed-text font-medium mb-1">Share “{selGoalOrBook.name}”</p>
                <p className="text-[11px] themed-text-muted mb-3">Exports a plain file anyone can add to their Phōkii — or hand to any LLM. Everything travels as To Do. Your landing lines and stars never leave, ever.</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-[12px] themed-text mb-3">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={shareCharter} onChange={e => setShareCharter(e.target.checked)} /> Plans (the “why”)</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={shareNotes} onChange={e => setShareNotes(e.target.checked)} /> Your context notes</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={shareDates} onChange={e => setShareDates(e.target.checked)} /> Your do/due dates</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={shareCompleted} onChange={e => setShareCompleted(e.target.checked)} /> Finished actions, reset to To Do</label>
                </div>
                <p className="text-[11px] themed-text-muted mb-3">Travels: <b>{b.length} project{b.length === 1 ? '' : 's'}, {nActions} action{nActions === 1 ? '' : 's'}</b> · titles, next steps, checklists (unchecked){shareCharter ? ', plans' : ''}{shareNotes ? ', notes' : ''}{shareDates ? ', dates' : ''}.</p>
                <div className="flex items-center gap-3">
                  <button onClick={() => downloadShare(selGoalOrBook)} className="px-3 py-1.5 text-[12px] font-semibold rounded-lg themed-button-primary">Export goal file</button>
                  <button onClick={() => setSharing(false)} className="text-[11.5px] themed-text-muted hover:themed-text">close</button>
                  {shareDone && <span className="text-[11px]" style={{ color: 'var(--color-success)' }}>Exported — it's a plain file. Nothing left your account.</span>}
                </div>
              </div>
            );
          })()}
          {closing && (
            <div className="my-3 rounded-lg border themed-border p-4" style={{ borderLeft: '3px solid var(--color-warning)' }}>
              <p className="text-[12px] themed-text mb-2 font-medium">Complete “{selGoalOrBook.name}” — how did it end?</p>
              <input autoFocus type="text" value={landingText} onChange={e => setLandingText(e.target.value)}
                placeholder="One honest line — shipped? dropped? overtaken by events?"
                className="w-full text-[13px] themed-text bg-transparent border themed-border rounded-lg px-3 py-2 focus:outline-none mb-2" />
              {s.open > 0 && <p className="text-[11px] themed-text-muted mb-2">{s.open} open action{s.open === 1 ? '' : 's'} will be archived with it — restorable, searchable, never erased.</p>}
              <div className="flex gap-2">
                <button onClick={() => completeGoal(selGoalOrBook)} className="px-3 py-1.5 text-[12px] font-semibold rounded-lg themed-button-primary">Complete goal</button>
                <button onClick={() => { setClosing(false); setLandingText(''); }} className="px-3 py-1.5 text-[12px] themed-text-muted hover:themed-text">Cancel</button>
              </div>
            </div>
          )}

          {/* ── the pages: project and action stand side by side ── */}
          <div className="flex gap-8 items-start">

          {/* ── page (a): the goal itself ──
              This used to print a computed "Next" — first open action by sort
              order, star ignored. Law 4: the ✦ is the only forward pointer.
              Now it prints the stars, each grounded in its project, exactly
              like the cover — and where there are none, it says so and stops. */}
          {!openAction && !selProject && !pageTwo && (<div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest themed-text-muted font-semibold mb-2">Starred</p>
            {starredHere.length > 0 ? starredHere.map(a => {
              const aProj = projects.find(p => p.id === a.linkId);
              return (
                <button key={a.id}
                  onClick={() => { if (aProj) setSel({ type: 'project', id: aProj.id }); setOpenActionId(a.id); }}
                  className="text-left block w-full group mb-3" title="Read this action">
                  <span className="block text-[19px] leading-snug themed-text group-hover:underline underline-offset-2">
                    <span className="mr-1.5" style={{ color: 'var(--color-pho)' }}>✦</span>{a.text}
                  </span>
                  {(aProj || a.dueDate) && (
                    <span className="block text-[11px] themed-text-muted mt-1">
                      {[aProj ? `from ${aProj.name}` : null, a.dueDate ? fmtCivil(a.dueDate) : null].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </button>
              );
            }) : (
              <p className="italic text-[13px] themed-text-muted leading-relaxed" style={{ maxWidth: '38ch' }}>
                Nothing starred yet. ✦ an action when it matters today — Phōkii never picks one for you.
              </p>
            )}
            <p className="text-[10px] uppercase tracking-widest themed-text-muted font-semibold mt-8 mb-2">Plan</p>
            <div className="foreword-compact">
              <ActionEditor key={`obj-${selGoalOrBook.id}`} content={selGoalOrBook.notes || ''}
                onSave={(md) => updateGoal(selGoalOrBook.id, { notes: md })} />
            </div>
          </div>)}

          {/* ── page (b): the project — stays open beside its action ── */}
          {selProject && (() => {
            const rows = [...actions.filter(projActions(selProject.id)), ...completedActions.filter(projActions(selProject.id))];
            return (
            <div className="min-w-0" style={{
              flex: openAction ? '0 0 340px' : '1 1 auto',
              maxWidth: openAction ? '340px' : '820px',
              transition: 'flex-basis .28s ease, max-width .28s ease',
            }}>
              {/* No breadcrumb here: the goal is already open on the left, and
                  its cover and plan both lead back to it. Moving a project to
                  another goal is rare, so it waits under the ··· rather than
                  standing beside the name looking like a decision. */}
              <div className="flex items-start justify-between gap-3">
                <InlineName value={selProject.name} onSave={(t) => updateProject(selProject.id, { name: t })}
                  className="text-2xl font-bold themed-text flex-1 leading-snug" />
                {!openAction && (
                  <div ref={pMenuRef} className="relative flex-shrink-0 mt-1.5">
                    <button onClick={() => { setPMenuOpen(o => !o); setDoneAsk(false); setDoneLanding(''); }}
                      aria-expanded={pMenuOpen} aria-haspopup="menu"
                      className="px-1.5 py-0.5 rounded text-[13px] themed-text-muted hover:themed-text transition-colors"
                      title="More">···</button>
                    {pMenuOpen && (
                      <div role="menu" className="absolute right-0 top-6 themed-background border themed-border rounded-lg shadow-lg py-1 z-30 min-w-[190px]">
                        {doneAsk ? (
                          <div className="px-3 py-2" style={{ minWidth: 230 }}>
                            <p className="text-[11px] themed-text-muted mb-1.5">How did it end? One line, for the record.</p>
                            <input autoFocus value={doneLanding}
                              onChange={(e) => setDoneLanding(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') { e.stopPropagation(); setDoneAsk(false); setDoneLanding(''); return; }
                                if (e.key !== 'Enter') return;
                                const goalId = selProject.goalId;
                                updateProject(selProject.id, { active: false, landing: doneLanding.trim() || null, closedDate: new Date().toISOString() });
                                setDoneAsk(false); setDoneLanding('');
                                go(goalId != null ? { type: 'goal', id: goalId } : null);
                              }}
                              placeholder="released to prod — no follow-ons"
                              className="w-full text-[12.5px] themed-text bg-transparent border themed-border rounded-md px-2 py-1.5 focus:outline-none placeholder:themed-text-muted" />
                          </div>
                        ) : (<>
                        <button onClick={() => { setDoneAsk(true); setDoneLanding(''); }}
                          className="w-full text-left px-3 py-1.5 text-[12.5px] font-semibold themed-text hover:themed-surface-hover"
                          title="Finish this project — it leaves the wall whole, tasks kept as they are">
                          Done — released…
                        </button>
                        <div className="border-t themed-border-light my-1" />
                        <p className="px-3 py-1 text-[10px] uppercase tracking-widest themed-text-muted font-semibold">Move to another goal</p>
                        {activeGoals.filter(o => o.id !== selProject.goalId).map(o => (
                          <button key={o.id}
                            onClick={() => { setPMenuOpen(false); updateProject(selProject.id, { goalId: o.id }); }}
                            className="w-full text-left px-3 py-1.5 text-[12.5px] themed-text hover:themed-surface-hover truncate">
                            {o.name}
                          </button>
                        ))}
                        {selProject.goalId != null && (
                          <button onClick={() => { setPMenuOpen(false); updateProject(selProject.id, { goalId: null }); }}
                            className="w-full text-left px-3 py-1.5 text-[12.5px] themed-text-muted hover:themed-surface-hover border-t themed-border-light mt-1 pt-2">
                            No goal
                          </button>
                        )}
                        </>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <FactLine filter={projActions(selProject.id)} />
              <p className="text-[10px] uppercase tracking-widest themed-text-muted font-semibold mt-6 mb-2">Plan</p>
              <div className="foreword-compact">
                <ActionEditor key={`proj-${selProject.id}`} content={selProject.notes || ''}
                  onSave={(md) => updateProject(selProject.id, { notes: md })} />
              </div>
              <p className="text-[10px] uppercase tracking-widest themed-text-muted font-semibold mt-8 mb-2">Actions</p>
              {rows.length === 0 && <p className="text-[13px] themed-text-muted">No actions here yet.</p>}
              {rows.map(g => (
                <button key={g.id} onClick={() => setOpenActionId(g.id)}
                  className={`w-full text-left flex items-center gap-2.5 py-2 border-b themed-border-light px-1 transition-colors ${openActionId === g.id ? 'themed-surface' : 'hover:themed-surface-hover'}`}
                  style={openActionId === g.id ? { boxShadow: 'inset 2px 0 0 var(--color-primary)' } : {}}>
                  <span className={`text-[13px] flex-1 min-w-0 truncate ${g.completed ? 'opacity-60' : ''} themed-text`}>
                    {g.pho && <span className="mr-1" style={{ color: 'var(--color-pho)' }}>✦</span>}{g.text}
                  </span>
                  {g.status === 'doing' && !g.completed && <span className="text-[10px] themed-text-muted flex-shrink-0">{STATE_WORDS.doing}</span>}
                  <span className="text-[10px] themed-text-muted flex-shrink-0">{timeAgo(g.lastTouchedAt || g.completedDate)}</span>
                </button>
              ))}
              {/* You are already inside the project, so the parent is never in doubt */}
              <input
                ref={newActionRef}
                key={`na-${selProject.id}-${rows.length}`}
                placeholder="+ New action…  (N)"
                title={`New action in ${selProject.name}`}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { e.target.value = ''; e.target.blur(); return; }
                  if (e.key !== 'Enter') return;
                  const v = e.target.value.trim();
                  if (!v) return;
                  const id = addActionToProject(selProject.id, v);
                  e.target.value = '';
                  if (id != null) setOpenActionId(id);
                }}
                className="w-full text-[13px] themed-text bg-transparent border-b themed-border-light focus:outline-none py-2 px-1 mt-1"
                style={{ borderBottomStyle: 'dashed' }} />
            </div>);
          })()}

          {/* ── recto state (c): an action, read as a page of this book ── */}
          {/* The same <ActionPage/> the workbench pane renders — one action
              surface, everywhere. The book only supplies the way out. */}
          {openAction && (
            <div className="flex-1 min-w-0" style={{ minWidth: "380px" }}>
              <ActionPage
                action={openAction}
                projects={projects}
                goals={goals}
                leading={
                  <button onClick={() => setOpenActionId(null)}
                    className="text-[11px] themed-text-muted hover:themed-text"
                    title="Close this action">✕ close</button>
                }
                updateAction={updateAction}
                setActionStatus={setActionStatus}
                toggleActionPho={toggleActionPho}
                toggleActionComplete={toggleActionComplete}
                addChecklistItem={addChecklistItem}
                onDelete={(id) => { if (deleteAction(id)) setOpenActionId(null); }}
                onOpenGoal={(id) => go({ type: "goal", id })}
                onOpenProject={(id) => go({ type: "project", id })}
                timeAgo={timeAgo}
                onEditorFocus={onWriteFullscreen ? () => onWriteFullscreen(openAction.id) : undefined}
              />
            </div>
          )}


          {/* ── the road behind ── */}
          {pageTwo && !openAction && !selProject && (
            <div className="flex-1 min-w-0" style={{ maxWidth: '60ch' }}>
              <p className="text-[10px] uppercase tracking-widest themed-text-muted font-semibold mb-2">Finished</p>
              {trail.length === 0 && (
                <p className="italic text-[13px] themed-text-muted leading-relaxed">
                  Nothing here yet. Finish something and your note about it shows up here.
                </p>
              )}
              {trail.slice(0, 20).map(g => (
                <button key={g.id} onClick={() => setOpenActionId(g.id)}
                  className="block w-full text-left py-3 border-b themed-border-light group">
                  <span className="block italic text-[13.5px] themed-text leading-relaxed group-hover:underline underline-offset-2">“{g.resolutionComment}”</span>
                  <span className="block text-[11px] themed-text-muted mt-1">— you, {timeAgo(g.completedDate || g.lastTouchedAt)}</span>
                </button>
              ))}
              <div className="mt-8">
                <button onClick={() => setPageTwo(false)} className="text-[12px] themed-text-muted hover:themed-text">⟵ Back to the plan</button>
              </div>
            </div>
          )}

          </div>{/* /pages */}
          </div>{/* /recto */}
        </div>);
      })()}

      {!bookGoal && (
        <button onClick={goHome} className="text-[11px] themed-text-muted hover:themed-text">‹ Back</button>
      )}
    </div>
  );
}
